import { useState } from 'react';
import { TVoiceModel } from '@/types/TVoiceModel';
import { SelectedVoice } from '@/interfaces/IVoice';
import { defaultVoiceModel } from '@/utils/defaultVoiceModal';

export const useVoiceConfig = (
	data: TVoiceModel,
	englishVoiceConfig: SelectedVoice | null | undefined,
	otherVoiceConfig: SelectedVoice | null | undefined,
) => {
	const determineInitialVoiceModel = () => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return (
				englishVoiceConfig?.voiceConfig?.voiceModel ||
				data?.model ||
				defaultVoiceModel({ language: data?.language, provider: data?.provider })
			);
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return (
				otherVoiceConfig?.voiceConfig?.voiceModel ||
				data?.model ||
				defaultVoiceModel({ language: data?.language, provider: data?.provider })
			);
		}
		return data?.model || defaultVoiceModel({ language: data?.language, provider: data?.provider });
	};

	const [voiceModel, setVoiceModel] = useState(determineInitialVoiceModel);
	const [speed, setSpeed] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.speed || 'normal';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.speed || 'normal';
		}
		return 'normal';
	});
	const [speedRime, setSpeedRime] = useState(1);
	const [customPauses, setCustomPauses] = useState(false);
	const [customPronunciation, setCustomPronunciation] = useState(false);
	const [temperature, setTemperature] = useState(0.73);

	const [emotion, setEmotion] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.emotion || 'positivity';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.emotion || 'positivity';
		}
		return 'positivity';
	});

	const [level, setLevel] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.level || '';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.level || '';
		}
		return '';
	});

	// Add new state variables for OpenAI

	const [accent, setAccent] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.accent || 'american';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.accent || 'american';
		}
		return 'american';
	});

	const [emotionalRange, setEmotionalRange] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.emotionalRange || 'neutral';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.emotionalRange || 'neutral';
		}
		return 'neutral';
	});

	const [intonation, setIntonation] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.intonation || 'expressive';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.intonation || 'expressive';
		}
		return 'expressive';
	});

	const [impression, setImpression] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.impression || 'receptionist';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.impression || 'receptionist';
		}
		return '';
	});

	const [tone, setTone] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.tone || 'professional';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.tone || 'professional';
		}
		return 'professional';
	});

	const [whispering, setWhispering] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.whispering || 'off';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.whispering || 'off';
		}
		return 'off';
	});

	const [voicePrompt, setVoicePrompt] = useState(() => {
		if (englishVoiceConfig?.voiceId === data.voiceId) {
			return englishVoiceConfig?.voiceConfig?.whispering || '';
		} else if (otherVoiceConfig?.voiceId === data.voiceId) {
			return otherVoiceConfig?.voiceConfig?.whispering || '';
		}
		return '';
	});

	return {
		voiceModel,
		setVoiceModel,
		speed,
		setSpeed,
		speedRime,
		setSpeedRime,
		emotion,
		setEmotion,
		level,
		setLevel,
		accent,
		setAccent,
		emotionalRange,
		setEmotionalRange,
		intonation,
		setIntonation,
		impression,
		setImpression,
		tone,
		setTone,
		whispering,
		setWhispering,
		voicePrompt,
		setVoicePrompt,
		customPauses,
		setCustomPauses,
		customPronunciation,
		setCustomPronunciation,
		temperature,
		setTemperature,
	};
};
